/**
 * #725 — the live-trading hotkey remap rows.
 *
 * Sits under the two Live Trading toggles in "Trading defaults", behind the
 * same `lt2Access` gate, and reads every label, default, and rule from the
 * canonical registry (`lib/trading/liveHotkeyRegistry`) so this panel and the
 * key listener can never disagree about what is bound.
 *
 * ### Why a captured KEYSTROKE and not a text field
 *
 * The obvious build is an input the trader types a letter into. It is the
 * wrong one, and #312/#607 are why: an `<input>` sanitizes or reinterprets
 * what was typed before any handler runs, so what gets stored is not reliably
 * what was pressed — and on #607 that shipped a silent 100x widening of a live
 * safety limit. Here the trader's intent IS a keystroke, so we take the
 * keystroke: `keydown` in the CAPTURE phase, `preventDefault` +
 * `stopImmediatePropagation`, and `e.key` straight into the registry's
 * normalizer. Nothing between the physical press and the stored value.
 *
 * Capture-phase interception is also the safety property. Settings does not
 * mount `Lt2HotkeyListener` today, but it costs nothing to guarantee that a
 * key pressed while REBINDING can never also be routed as that key's action —
 * a trader assigning W must not fire an Attack in the act of assigning it.
 *
 * ### Refusals are specific, and they are the instruction
 *
 * Every refusal names the reason and what to do instead, the same discipline
 * the hotkey acks follow. A conflict names the action already holding the key,
 * because "that key is taken" without saying by what leaves the trader
 * guessing at their own settings.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useLiveHotkeysStore } from '@/stores/liveHotkeys';
import { useLiveHotkeyMap } from '@/hooks/useLiveHotkeys';
import {
  LIVE_HOTKEY_ACTION_IDS,
  LIVE_HOTKEY_REGISTRY,
  activeHotkeyBindings,
  conflictingAction,
  hotkeyDisplayLabel,
  isActionActive,
  keyRefusalReason,
  normalizeHotkey,
  type LiveHotkeyActionId,
  type LiveHotkeyGrants,
} from '@/lib/trading/liveHotkeyRegistry';
import { useAuthStore } from '@/stores/authStore';

/** Keys that end a capture without binding anything. Escape is the universal
 *  "never mind"; it is also not bindable, so treating it as cancel costs no
 *  reachable choice. */
const CANCEL_KEYS = new Set(['Escape']);

export function LiveHotkeyRemapPanel() {
  // Ownership-gated read, direct writes: the panel can only ever edit the
  // signed-in trader's own mapping.
  const liveHotkeys = useLiveHotkeyMap();
  /**
   * #725 review 2 — the panel reasons about the SAME grants the listener does.
   * An action the trader has no grant for is inactive: it owns no key, blocks
   * no key, and cannot be edited here. Subscribed (not read once) so a grant
   * changed by a manager mid-session re-renders the rows rather than leaving a
   * stale "not granted" that still blocks the key.
   */
  const attackAccess = useAuthStore((st) => st.user?.attackAccess ?? false);
  const grants: LiveHotkeyGrants = { attack: attackAccess };
  const bindings = activeHotkeyBindings(liveHotkeys, grants);
  const setLiveHotkey = useLiveHotkeysStore((s) => s.setHotkey);
  const resetLiveHotkeys = useLiveHotkeysStore((s) => s.reset);

  const [capturingId, setCapturingId] = useState<LiveHotkeyActionId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  // The listener is registered per capture, so it cannot fire when no row is
  // waiting for a key. Read the live values from a ref rather than the closure
  // so re-renders during a capture do not churn the listener (the latest-ref
  // pattern from useDismissibleLayer; synced in an effect, never in render).
  // `grants` rides along: a manager revoking a grant mid-capture must make the
  // key it used to hold immediately claimable, not leave the handler refusing
  // against the grants that were true when the capture started.
  const latest = useRef({ capturingId, liveHotkeys, setLiveHotkey, grants });
  useEffect(() => {
    latest.current = { capturingId, liveHotkeys, setLiveHotkey, grants };
  }, [capturingId, liveHotkeys, setLiveHotkey, grants.attack]);

  useEffect(() => {
    if (capturingId == null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const id = latest.current.capturingId;
      if (id == null) return;
      // Claim the keystroke before anything else can act on it — including a
      // hotkey listener that would otherwise route this very key as an order.
      e.preventDefault();
      e.stopImmediatePropagation();

      // Mid-composition the keystroke belongs to the IME, not to us — the same
      // guard `handleLt2Keydown` applies, and for the same reason: a character
      // an IME is still assembling is not a key the trader chose. `keyCode 229`
      // is the pre-`isComposing` signal some IMEs still send. Third case of the
      // same defect review found: capture must not accept what runtime handles
      // differently.
      if (e.isComposing || e.keyCode === 229) return;

      if (CANCEL_KEYS.has(e.key)) {
        setCapturingId(null);
        setError(null);
        return;
      }
      // A bare modifier is a chord in progress, not a choice — keep waiting
      // rather than refusing, so a modifier pressed on the way to another key
      // does not produce a message mid-press.
      if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
        return;
      }

      // ⚠️ REVIEW FIX — capture and runtime must agree about what is reachable.
      //
      // `handleLt2Keydown` refuses EVERY event carrying a modifier (#609's
      // browser-interaction fence). So a key that can only be produced WITH a
      // modifier can never fire, however happily it stores. Shift+/ is the
      // case review found: it arrives as `?`, passes every other rule, and is
      // displayed as a live binding the board will never answer to.
      //
      // The check is on the MODIFIER STATE of the accepting keystroke rather
      // than on the character, because whether a character needs Shift depends
      // on the keyboard layout and we cannot know that. Refusing what was
      // typed with a modifier is exactly the runtime rule, so every accepted
      // mapping is reachable by construction — and the trader is told to press
      // it without the modifier, which for a plain letter simply works.
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) {
        setError(
          'That needs a modifier key, and modified keys cannot place orders. Press a key on its own.',
        );
        return;
      }

      const key = normalizeHotkey(e.key);
      const refusal = keyRefusalReason(key);
      if (refusal != null) {
        setError(refusal);
        return;
      }
      const clash = conflictingAction(latest.current.liveHotkeys, key, id, latest.current.grants);
      if (clash != null) {
        setError(
          `${hotkeyDisplayLabel(key)} is already ${LIVE_HOTKEY_REGISTRY[clash].label.toLowerCase()}. Pick another key, or change that action first.`,
        );
        return;
      }
      latest.current.setLiveHotkey(id, key);
      setCapturingId(null);
      setError(null);
      setConfirmation(
        `${LIVE_HOTKEY_REGISTRY[id].label} is now ${hotkeyDisplayLabel(key)}.`,
      );
    };
    // Capture phase on `window`: ahead of every bubble-phase handler in the app.
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [capturingId]);

  const startCapture = useCallback((id: LiveHotkeyActionId) => {
    setCapturingId(id);
    setError(null);
    setConfirmation(null);
  }, []);

  const onReset = useCallback(() => {
    resetLiveHotkeys();
    setCapturingId(null);
    setError(null);
    setConfirmation('Every live-trading key is back to its default.');
  }, [resetLiveHotkeys]);

  const isCustomized = LIVE_HOTKEY_ACTION_IDS.some(
    (id) => liveHotkeys[id] != null && liveHotkeys[id] !== LIVE_HOTKEY_REGISTRY[id].defaultKey,
  );

  return (
    <div data-testid="live-hotkey-remap">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label className="text-muted-foreground block text-xs font-medium">
          Live Trading: key mapping
        </label>
        <button
          type="button"
          onClick={onReset}
          disabled={!isCustomized}
          title={
            isCustomized
              ? 'Put every live-trading key back to its default (A / D / W / E / Space).'
              : 'Every key is already at its default.'
          }
          className="text-muted-foreground hover:text-foreground disabled:text-muted-foreground/40 flex shrink-0 items-center gap-1 text-xs font-medium disabled:cursor-not-allowed"
        >
          <RotateCcw size={12} aria-hidden />
          Reset to defaults
        </button>
      </div>

      <div className="border-border/50 divide-border/40 divide-y rounded-md border">
        {LIVE_HOTKEY_ACTION_IDS.map((id) => {
          const meta = LIVE_HOTKEY_REGISTRY[id];
          // An inactive action is absent from the binding table by design, so
          // it renders with no key at all rather than advertising one it does
          // not own and could not fire.
          const active = isActionActive(id, grants);
          const bound = bindings.get(id) ?? null;
          const unavailable = !meta.implemented
            ? 'Reserved for a later release. It holds no key, so its default is free for another action until it ships.'
            : !active
              ? 'Your account does not have this permission, so it holds no key — its default is free for another action. Ask a manager to grant it.'
              : null;
          const capturing = capturingId === id;
          return (
            <div key={id} className="flex items-start justify-between gap-3 px-2.5 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium">{meta.label}</span>
                  {!active && (
                    <span
                      className="text-muted-foreground border-border/50 bg-muted/40 rounded border px-1 py-px text-[10px]"
                      title={unavailable ?? undefined}
                    >
                      {meta.implemented ? 'not granted' : 'soon'}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground mt-0.5 text-xs leading-snug">
                  {meta.description}
                </p>
              </div>
              <button
                type="button"
                // An action the trader cannot perform is DISPLAY ONLY, and it
                // holds no key: binding one would let a trader point a key at
                // nothing today and have it start placing orders the day the
                // capability arrives — a surprise nobody opted into. It becomes
                // editable when it becomes usable (#724 flips `implemented`; a
                // manager flips the grant).
                disabled={!active}
                onClick={() => (capturing ? setCapturingId(null) : startCapture(id))}
                aria-label={
                  !active
                    ? `Key for ${meta.label}: none. ${unavailable ?? ''}`
                    : capturing
                      ? `Press the new key for ${meta.label}, or Escape to cancel`
                      : `Change the key for ${meta.label}. Currently ${bound == null ? 'not set' : hotkeyDisplayLabel(bound)}.`
                }
                className={`shrink-0 rounded-md border px-2 py-1 font-mono text-xs transition-colors ${
                  capturing
                    ? 'border-blue-500/60 bg-blue-500/15 text-blue-600 dark:text-blue-400'
                    : !active
                      ? 'border-border/40 bg-muted/30 text-muted-foreground/50 cursor-not-allowed'
                      : 'border-border/50 bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {capturing
                  ? 'Press a key…'
                  : !active
                    ? '—'
                    : bound == null
                      ? 'Not set'
                      : hotkeyDisplayLabel(bound)}
              </button>
            </div>
          );
        })}
      </div>

      {/* One live region for both outcomes, so a screen reader announces the
          refusal or the confirmation without the row order shifting. */}
      <p
        role="status"
        aria-live="polite"
        className={`mt-1.5 text-xs leading-snug ${error ? 'text-negative' : 'text-muted-foreground'}`}
      >
        {error ??
          confirmation ??
          'Click a key to change it, then press the key you want. Keys reserved for moving around — Enter, Escape, Tab, the arrows — are not available.'}
      </p>
    </div>
  );
}
